import type { SortState } from './types'
import type {
  CurrentUser,
  PerilRecordDetail,
  PerilSearchResponse,
  PerilSuggestResponse,
  SearchCriteria,
  SuggestField,
} from './types'

/** Empty in development: Vite proxies /api to the FastAPI port. */
const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')

/** Liveness only, and the one route that never carries a token. */
const OPEN_PATHS = ['/api/health']

/**
 * What the client needs from the auth layer. AuthState satisfies this, so the
 * whole context can be handed straight to any call below.
 */
export interface ApiAuth {
  getToken: (options?: { forceRefresh?: boolean }) => Promise<string | null>
  reauthenticate: () => Promise<void>
}

export class ApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

/**
 * A backstop, not a policy. The API bounds its own database query and answers
 * with a 504 and advice when a search is too broad, which is the failure an
 * underwriter should normally see. This exists for the case where no answer
 * arrives at all: a dropped connection leaves the spinner turning forever
 * otherwise, which is what a broad Sydney search looked like on 28/08/2026
 * before the query timeout existed.
 *
 * Set above the API's own timeout so the server's actionable message wins the
 * race whenever the server is still alive to send one.
 */
const REQUEST_TIMEOUT_MS = 90_000

/** True for a request cancelled by an AbortController, which is not a failure. */
export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

type QueryValue = string | number | boolean | undefined

async function send(
  url: string,
  token: string | null,
  signal?: AbortSignal,
): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`

  // The caller's signal, which cancels a superseded typeahead lookup, combined
  // with the backstop. Either aborts the request.
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS)
  const combined = signal ? AbortSignal.any([signal, timeout]) : timeout

  try {
    return await fetch(url, { headers, signal: combined })
  } catch (error) {
    // A superseded typeahead lookup is cancelled, not broken. Let it through so
    // the caller can drop it rather than report it. The caller's own signal is
    // the only one that means that: the backstop firing is a real failure.
    if (isAbortError(error) && signal?.aborted) throw error
    if (timeout.aborted) {
      throw new ApiError(
        'The search did not finish in time. Add a state or postcode to narrow it.',
        504,
      )
    }
    if (isAbortError(error)) throw error
    throw new ApiError('The service could not be reached. Check your connection.', 0)
  }
}

async function get<T>(
  path: string,
  params: Record<string, QueryValue>,
  auth: ApiAuth,
  signal?: AbortSignal,
): Promise<T> {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue
    search.set(key, String(value))
  }
  const query = search.toString()
  const url = `${API_BASE}${path}${query ? `?${query}` : ''}`
  const needsToken = !OPEN_PATHS.includes(path)

  let response = await send(url, needsToken ? await auth.getToken() : null, signal)

  // A rejected token gets one forced refresh and one retry, never a loop. If the
  // fresh token is rejected too, only signing in again can fix it.
  if (response.status === 401 && needsToken) {
    const refreshed = await auth.getToken({ forceRefresh: true })
    if (refreshed) {
      response = await send(url, refreshed, signal)
    }
    if (response.status === 401) {
      await auth.reauthenticate()
    }
  }

  if (!response.ok) {
    throw new ApiError(await friendlyMessage(response), response.status)
  }
  return (await response.json()) as T
}

/** One entry of FastAPI's 422 body. `loc` is ["query", "postcode"] or similar. */
interface ValidationEntry {
  loc?: unknown[]
  msg?: string
}

/**
 * Turn a failed response into something an underwriter can act on, and failing
 * that, into something support can trace.
 *
 * This used to collapse everything it did not recognise into "The peril search
 * could not be completed. Try again." That sentence hid two entirely different
 * problems and told nobody which one had happened:
 *
 * - **A 422.** FastAPI reports validation failures with `detail` as an *array*,
 *   not a string, so the check for a string fell through and the field at fault
 *   was never named.
 * - **A response that did not come from this API at all.** A restarting worker,
 *   a gateway or a proxy answers with HTML, so parsing it as JSON threw and the
 *   status code, the only real information available, was thrown away with it.
 *
 * That happened in production on 30/08/2026: a search failed and the message
 * could not say whether the criteria were rejected or the service was down.
 */
async function friendlyMessage(response: Response): Promise<string> {
  if (response.status === 401) return 'Your session has expired. Sign in again.'
  if (response.status === 403) return 'You do not have access to the peril database.'
  if (response.status === 404) return 'That peril record could not be found.'

  const detail = await readDetail(response)

  // The API's own message. These are written for an underwriter, and include
  // the timeout advice and the configuration warning, so they win.
  if (typeof detail === 'string' && detail.trim()) return detail

  if (Array.isArray(detail)) {
    const fields = detail
      .map((entry) => describeField(entry as ValidationEntry))
      .filter((name): name is string => Boolean(name))
    if (fields.length > 0) {
      return `The search was rejected because of ${joinWords(fields)}. Check those values and try again.`
    }
    return 'One of the search criteria was rejected. Check the values and try again.'
  }

  // Nothing usable in the body, so the response did not come from this API.
  // The status is reported rather than swallowed: it is what tells support
  // whether the service was restarting, overloaded or unreachable.
  return unexpectedResponseMessage(response.status)
}

/** The response body's `detail`, or undefined when there is no JSON at all. */
async function readDetail(response: Response): Promise<unknown> {
  try {
    const body = (await response.json()) as { detail?: unknown }
    return body?.detail
  } catch {
    return undefined
  }
}

/** "Postcode" from ["query", "postcode"], for a 422 that names a field. */
function describeField(entry: ValidationEntry): string | null {
  const location = Array.isArray(entry.loc) ? entry.loc : []
  const last = location[location.length - 1]
  if (typeof last !== 'string' || last === 'query' || last === 'body') return null
  // camelCase on the wire, so "streetName" becomes "Street Name".
  const spaced = last.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

function joinWords(values: string[]): string {
  if (values.length === 1) return values[0] as string
  return `${values.slice(0, -1).join(', ')} and ${values[values.length - 1]}`
}

/**
 * A 5xx that carried no message of ours. The status is included on purpose: it
 * is the one thing that distinguishes "the service is restarting" from "the
 * service is refusing", and an underwriter reporting it saves a support call.
 */
function unexpectedResponseMessage(status: number): string {
  if (status === 502 || status === 503) {
    return `The peril data service is restarting or busy, so this search did not run (error ${status}). Wait a moment and try again. If it keeps happening, report the time to IT support.`
  }
  if (status === 504) {
    return `The search took too long for the service to answer (error ${status}). Add a state or postcode to narrow it.`
  }
  if (status >= 500) {
    return `The peril data service failed to answer this search (error ${status}). Report the time to IT support.`
  }
  return `The peril search could not be completed (error ${status}). Report the time to IT support.`
}

export function searchPeril(
  criteria: SearchCriteria,
  page: number,
  sort: SortState,
  auth: ApiAuth,
  signal?: AbortSignal,
): Promise<PerilSearchResponse> {
  const isAddress = criteria.searchBy === 'address'
  return get<PerilSearchResponse>(
    '/api/peril/search',
    {
      searchBy: criteria.searchBy,
      state: isAddress ? criteria.state : undefined,
      postcode: isAddress ? criteria.postcode.trim() : undefined,
      suburb: isAddress ? criteria.suburb.trim() : undefined,
      streetName: isAddress ? criteria.streetName.trim() : undefined,
      streetNumber: isAddress ? criteria.streetNumber.trim() : undefined,
      unitNumber: isAddress ? criteria.unitNumber.trim() : undefined,
      propertyKey: isAddress ? undefined : criteria.propertyKey.trim(),
      strataPlanNumber: isAddress ? undefined : criteria.strataPlanNumber.trim(),
      mostReliableOnly: criteria.mostReliableOnly,
      includeUnits: criteria.includeUnits,
      includeBushfire: criteria.includeBushfire,
      page,
      sortBy: sort.sortBy ?? undefined,
      sortDir: sort.sortBy ? sort.sortDir : undefined,
    },
    auth,
    signal,
  )
}

/**
 * Values to offer under one address field. Every other criterion narrows the
 * list, so a postcode lookup with NSW selected offers NSW postcodes only.
 */
export function fetchPerilSuggestions(
  field: SuggestField,
  term: string,
  criteria: SearchCriteria,
  auth: ApiAuth,
  signal?: AbortSignal,
): Promise<PerilSuggestResponse> {
  return get<PerilSuggestResponse>(
    '/api/peril/suggest',
    {
      field,
      term,
      state: criteria.state,
      // A field never narrows its own list.
      postcode: field === 'postcode' ? undefined : criteria.postcode.trim(),
      suburb: field === 'suburb' ? undefined : criteria.suburb.trim(),
      streetName: field === 'streetName' ? undefined : criteria.streetName.trim(),
    },
    auth,
    signal,
  )
}

/**
 * The State dropdown's values, from `spGetLocalityStates`.
 *
 * Asked for once per session rather than as the underwriter types: State is a
 * select, not a typeahead. An empty term returns the whole list, which already
 * includes the ALL sentinel because the procedure supplies it.
 */
export async function fetchStates(auth: ApiAuth, signal?: AbortSignal): Promise<string[]> {
  const response = await get<PerilSuggestResponse>(
    '/api/peril/suggest',
    { field: 'state', term: '', limit: 50 },
    auth,
    signal,
  )
  return response.items
}

export function fetchPerilRecord(recordRef: string, auth: ApiAuth): Promise<PerilRecordDetail> {
  return get<PerilRecordDetail>(`/api/peril/record/${encodeURIComponent(recordRef)}`, {}, auth)
}

export function fetchCurrentUser(auth: Pick<ApiAuth, 'getToken'>): Promise<CurrentUser> {
  return get<CurrentUser>('/api/me', {}, { reauthenticate: async () => undefined, ...auth })
}
